/**
 * DailyLogSection — Section "Saisie quotidienne & bloquants" (P1)
 *
 * Cf spec `docs/superpowers/specs/2026-05-21-journal-quotidien-equipe-design.md` §2.1.
 *
 * Props:
 *   - userEmail (string, requis) : email de l'utilisateur dont on saisit le journal.
 *
 * Endpoints consommés (cf §4 spec) — TOUS sous /api/team/daily-log/:
 *   GET    {user}/{date}                  → état + tickets tracés
 *   POST   {user}/{date}/entry            → ajoute entry hors-ticket
 *   PUT    {user}/{date}/entry/{id}       → édite entry
 *   DELETE {user}/{date}/entry/{id}       → supprime entry
 *   PUT    {user}/{date}/presence         → override présence
 *   POST   {user}/{date}/blocker          → ajoute bloquant
 *   PUT    {user}/{date}/blocker/{id}/resolve
 *   POST   {user}/{date}/validate         → validation finale
 *
 * Périmètre P1 :
 *   - 5 zones du mockup §2.1 (navigation jour, présence, tickets read-only,
 *     entries éditables inline, bloquants).
 *   - Bouton "Valider ma journée" disabled tant que unallocated > 15 min.
 *   - Verrou backend 409 sur journée validée — pas de re-tentative auto.
 *
 * Hors scope P1 :
 *   - Validation 3 paliers (toast 16h30, banner, modal bloquante) → P3-P4.
 *   - Édition admin pour autrui ("Remplir pour lui") → P2.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Plus,
  Trash2,
  Check,
  AlertTriangle,
  Calendar,
  FileText,
  AlertCircle,
  Monitor,
} from 'lucide-react';
import { apiFetch, readApiJson } from '../lib/api';
import { withArchivedCategory, resolveCategoryOnOrgChange } from '../lib/ticketHelpers';
import { useToast, useConfirm } from './TechComponents';
import AppelsNonClassesBloc from './AppelsNonClassesBloc';
import PassagesARelancerBloc from './numeros/PassagesARelancerBloc';
import DataList from './DataList';
import ConnexionsPostesDuJour from './ConnexionsPostes';
import { TASK_STATUS_LABELS, TASK_STATUS_COLORS } from '../constants/taskStatuses';
import {
  HDR_BTN,
  PRIMARY_BTN,
  SUCCESS_BTN,
  EDIT_INPUT,
  SELECT_STYLE,
  FIELD_LABEL,
  TH_STYLE,
  TD_STYLE,
} from '../styles/CommonStyles';

const mono = { fontFamily: "'IBM Plex Mono', monospace" };

// ── Tickets clients déjà tracés : colonnes du tableau ─────────────────────────
// Heure = création de la tâche (pas forcément l'heure de l'intervention).
function heureDe(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}
const LIBELLE_STATUT_TACHE = { ...TASK_STATUS_LABELS, in_progress: 'En cours', pending: 'En attente', cancelled: 'Annulé' };

const COLONNES_TACHES_SUIVIES = [
  { key: 'created_at', label: 'Heure', sortable: true, width: 70,
    sortValue: (t) => (t.created_at ? Date.parse(t.created_at) : null),
    render: (t) => <span style={mono} title="Heure de création de la tâche">{heureDe(t.created_at)}</span> },
  { key: 'ticket_number', label: 'Ticket', sortable: true, width: 80,
    render: (t) => (t.ticket_id ? (
      <Link to={`/tickets/${t.ticket_id}`} style={{ color: '#2563eb', fontWeight: 700, textDecoration: 'none', ...mono }}>
        #{t.ticket_number ?? t.ticket_id.slice(0, 8)}
      </Link>
    ) : <span style={{ color: '#94a3b8', ...mono }}>—</span>) },
  { key: 'client_name', label: 'Société', sortable: true, width: 150,
    render: (t) => t.client_name || <span style={{ color: '#94a3b8' }}>—</span> },
  { key: 'title', label: 'Ce qui a été écrit', sortable: true,
    sortValue: (t) => t.title || t.ticket_title || '',
    render: (t) => <span style={{ color: '#334155' }} title={t.ticket_title || ''}>{t.title || t.ticket_title || 'Sans titre'}</span> },
  { key: 'status', label: 'Statut', sortable: true, width: 100,
    sortValue: (t) => LIBELLE_STATUT_TACHE[t.status] || t.status || '',
    render: (t) => (t.status
      ? <span style={{ fontSize: 11, fontWeight: 600, color: TASK_STATUS_COLORS[t.status] || '#475569' }}>{LIBELLE_STATUT_TACHE[t.status] || t.status}</span>
      : <span style={{ color: '#94a3b8' }}>—</span>) },
  { key: 'time_spent_minutes', label: 'Temps', sortable: true, width: 80,
    render: (t) => (
      <span style={{ ...mono, fontWeight: 700, color: t.time_spent_minutes ? '#059669' : '#b45309' }}
        title={t.time_spent_minutes ? '' : 'Aucun temps saisi sur cette tâche'}>
        {fmtMinutes(t.time_spent_minutes || 0)}
      </span>
    ) },
];

// ── Helpers ─────────────────────────────────────────────────────────────────

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function shiftISODate(iso, deltaDays) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + deltaDays);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function fmtMinutes(min) {
  const v = Number(min);
  if (Number.isNaN(v)) return '—';
  // Gère le signe : -90 → "-1h30" plutôt que "-1h-30" (bug fix unallocated < 0)
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h === 0) return `${sign}${m} min`;
  if (m === 0) return `${sign}${h} h`;
  return `${sign}${h}h${String(m).padStart(2, '0')}`;
}

function computeActiveMinutes(start, end, lunch, coffee) {
  const parse = (s) => {
    if (!s || typeof s !== 'string') return null;
    const [hh, mm] = s.split(':');
    const h = parseInt(hh, 10);
    const m = parseInt(mm, 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;
    return h * 60 + m;
  };
  const s = parse(start);
  const e = parse(end);
  if (s === null || e === null) return 0;
  const delta = Math.max(0, e - s);
  return Math.max(0, delta - Math.max(0, Number(lunch) || 0) - Math.max(0, Number(coffee) || 0));
}

async function readApiError(response) {
  // Backend FastAPI renvoie {detail: "..."} sur HTTPException.
  try {
    const data = await response.clone().json();
    if (data && data.detail) return String(data.detail);
    if (data && data.error) return String(data.error);
  } catch {}
  return `${response.status} ${response.statusText}`;
}

// Motifs d'absence RH (hr_attendance_svc.VALID_ABSENCE_REASONS) en mots d'usage.
const ABSENCE_LIBELLES = {
  cp: 'congé payé',
  maladie: 'maladie',
  sans_solde: 'congé sans solde',
  enfant_malade: 'enfant malade',
  autre: 'autre motif',
};

// ── Composant ───────────────────────────────────────────────────────────────

export default function DailyLogSection({ userEmail, lockedDate = null, initialDate = null }) {
  const toast = useToast();
  const confirm = useConfirm();

  // `initialDate` (lien du bandeau de rappel) ouvre une journée sans l'épingler, contrairement à `lockedDate`.
  const [selectedDate, setSelectedDate] = useState(() => lockedDate || initialDate || todayISO());
  const [dailyLog, setDailyLog] = useState(null);
  const [trackedTasks, setTrackedTasks] = useState([]);
  const [trackedTotal, setTrackedTotal] = useState(0);
  // Absence RH du jour (congé approuvé…) : { reason } ou null. Jour sans présence (bug n°649).
  const [absence, setAbsence] = useState(null);
  const [loading, setLoading] = useState(false);

  // Cache organisations + catégories chargées une fois (rarement modifiées)
  const [organizations, setOrganizations] = useState([]);
  const [categoriesByOrg, setCategoriesByOrg] = useState({}); // { orgId: [{id,name,...}] }
  const [techUsers, setTechUsers] = useState([]); // destinataires bloquants

  // Brouillon présence — édition locale avant save (évite save sur chaque keypress)
  const [presenceDraft, setPresenceDraft] = useState({
    presence_start: '09:00',
    presence_end: '17:00',
    lunch_break_min: 60,
    coffee_break_min: 30,
  });
  const [savingPresence, setSavingPresence] = useState(false);
  // Indicateur "Brouillon enregistré à HH:MM" (Fix 5)
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // AbortController du dernier PUT présence en vol — évite race condition (Fix 4)
  const presenceAbortRef = useRef(null);

  // Brouillon "ajouter entry"
  const [newEntry, setNewEntry] = useState({
    organization_id: '',
    category_id: '',
    project_label: '',
    minutes: 30,
    comment: '',
  });
  const [addingEntry, setAddingEntry] = useState(false);

  // Bloquant binaire + brouillon nouveau bloquant
  const [hasBlockersUi, setHasBlockersUi] = useState(false);
  const [newBlocker, setNewBlocker] = useState({
    type: 'waiting',
    target_user_id: 'william.bernard@meteris.fr',
    description: '',
  });
  const [addingBlocker, setAddingBlocker] = useState(false);

  const [validating, setValidating] = useState(false);
  // Refus de la validation, écrit À CÔTÉ du bouton : rendue dans l'écran de blocage de 13 h
  // (z-index 100000), la saisie a ses toasts (z-index 9999) cachés dessous (05/10/2026).
  const [erreurValidation, setErreurValidation] = useState(null);
  // Le refus vaut pour le jour où il a été donné.
  useEffect(() => { setErreurValidation(null); }, [selectedDate]);

  // ── Chargement organisations + techs (une fois au mount) ──────────────────
  useEffect(() => {
    apiFetch('/api/crm/organizations')
      .then(readApiJson)
      .then((res) => setOrganizations(res.organizations || []))
      .catch(() => setOrganizations([]));
    apiFetch('/api/tk/users')
      .then(readApiJson)
      .then((res) => setTechUsers(res.users || []))
      .catch(() => setTechUsers([]));
  }, []);

  // ── Chargement catégories pour une org (lazy) ─────────────────────────────
  const ensureCategoriesLoaded = useCallback(
    (orgId) => {
      if (!orgId || categoriesByOrg[orgId]) return;
      apiFetch(`/api/tk/admin/categories?organization_id=${encodeURIComponent(orgId)}&active=true`)
        .then(readApiJson)
        .then((res) => {
          setCategoriesByOrg((prev) => ({ ...prev, [orgId]: res.categories || [] }));
        })
        .catch(() => {
          setCategoriesByOrg((prev) => ({ ...prev, [orgId]: [] }));
        });
    },
    [categoriesByOrg],
  );

  // ── Fetch daily log à chaque changement de date ───────────────────────────
  const fetchDailyLog = useCallback(async () => {
    if (!userEmail || !selectedDate) return;
    // Reset stale state AVANT le fetch (sinon flash 200-500ms des chiffres du jour précédent)
    setDailyLog(null);
    setTrackedTasks([]);
    setTrackedTotal(0);
    setAbsence(null);
    setHasBlockersUi(false);
    setLoading(true);
    try {
      const res = await apiFetch(
        `/api/team/daily-log/${encodeURIComponent(userEmail)}/${selectedDate}`,
      );
      if (!res.ok) {
        const msg = await readApiError(res);
        toast.error(`Chargement journal : ${msg}`);
        setDailyLog(null);
        setTrackedTasks([]);
        setTrackedTotal(0);
        return;
      }
      const data = await res.json();
      const dl = data.daily_log || null;
      setDailyLog(dl);
      setTrackedTasks(data.tracked_tasks || []);
      setTrackedTotal(data.tracked_total_minutes || 0);
      setAbsence(data.absence || null);
      if (dl) {
        setPresenceDraft({
          presence_start: dl.presence_start || '09:00',
          presence_end: dl.presence_end || '17:00',
          lunch_break_min: dl.lunch_break_min ?? 60,
          coffee_break_min: dl.coffee_break_min ?? 30,
        });
        setHasBlockersUi(Boolean(dl.has_blockers) || (dl.blockers || []).length > 0);
        // Charger les catégories de chaque org présente dans les entries (utile pour affichage)
        const orgs = new Set();
        (dl.entries || []).forEach((e) => e.organization_id && orgs.add(e.organization_id));
        orgs.forEach((o) => ensureCategoriesLoaded(o));
      }
    } catch (e) {
      toast.error(`Erreur réseau : ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [userEmail, selectedDate, toast, ensureCategoriesLoaded]);

  useEffect(() => {
    fetchDailyLog();
  }, [fetchDailyLog]);

  // Charger catégories pour l'org du formulaire "nouvelle entry" dès qu'elle change
  useEffect(() => {
    if (newEntry.organization_id) ensureCategoriesLoaded(newEntry.organization_id);
  }, [newEntry.organization_id, ensureCategoriesLoaded]);

  // Resync date épinglée si le parent change lockedDate (ex: DailyLogGuard)
  useEffect(() => { if (lockedDate) setSelectedDate(lockedDate); }, [lockedDate]);

  // ── Indicateurs live ──────────────────────────────────────────────────────
  const activeMinutes = absence ? 0 : (dailyLog?.active_minutes_total ?? 0);
  const entries = dailyLog?.entries || [];
  const blockers = dailyLog?.blockers || [];
  const entriesTotal = useMemo(
    () => entries.reduce((s, e) => s + (Number(e.minutes) || 0), 0),
    [entries],
  );
  const unallocated = activeMinutes - trackedTotal - entriesTotal;
  const isValidated = dailyLog?.validation_status === 'validated';
  // Jour d'absence : aucune présence à allouer, un éventuel ticket du jour n'est pas un « sur-alloué ».
  const overAllocated = !absence && unallocated < 0;

  // Nombre de catégories distinctes dans les entries (règle backend §3.3 — Fix 6)
  const entriesCategoryCount = useMemo(
    () => new Set(entries.map((e) => e.category_id).filter(Boolean)).size,
    [entries],
  );
  // Marker orange si 1 seule cat et total > 120 min (règle "≥ 2 cat au-delà de 2h")
  const categoryWarning = entriesTotal > 120 && entriesCategoryCount < 2;

  // Couleur badge unallocated — rouge si sur-alloué (négatif) OU > 60min,
  // orange si > 15min, vert sinon (tolérance OK)
  const unallocatedColor = overAllocated
    ? '#dc2626'
    : unallocated > 60
    ? '#dc2626'
    : unallocated > 15
    ? '#f59e0b'
    : '#059669';

  // ── Présence save (blur silencieux ou bouton explicite) ──────────────────
  // silent=true → onBlur (pas de toast, juste persiste). silent=false → bouton "Enregistrer".
  const savePresence = async ({ silent = false } = {}) => {
    if (!userEmail || !selectedDate || isValidated) return;
    // Abort la requête précédente encore en vol pour éviter une race
    // (4 onBlur successifs → seul le dernier doit gagner)
    if (presenceAbortRef.current) {
      try { presenceAbortRef.current.abort(); } catch {}
    }
    const ctrl = new AbortController();
    presenceAbortRef.current = ctrl;
    if (!silent) setSavingPresence(true);
    try {
      const res = await apiFetch(
        `/api/team/daily-log/${encodeURIComponent(userEmail)}/${selectedDate}/presence`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(presenceDraft),
          signal: ctrl.signal,
        },
      );
      if (!res.ok) {
        const msg = await readApiError(res);
        toast.error(`Présence : ${msg}`);
        return;
      }
      const data = await res.json();
      if (data.daily_log) setDailyLog(data.daily_log);
      setLastSavedAt(new Date());
      if (!silent) toast.success('Présence enregistrée');
    } catch (e) {
      // Abort silencieux : ne pas remonter d'erreur si on a abort soi-même
      if (e.name === 'AbortError') return;
      toast.error(`Erreur réseau présence : ${e.message}`);
    } finally {
      if (presenceAbortRef.current === ctrl) presenceAbortRef.current = null;
      if (!silent) setSavingPresence(false);
    }
  };
  // Wrapper utilisé par les onBlur — silencieux (pas de toast spam)
  const savePresenceSilent = () => savePresence({ silent: true });

  // Active recalculé localement pendant l'édition (avant save)
  const draftActive = computeActiveMinutes(
    presenceDraft.presence_start,
    presenceDraft.presence_end,
    presenceDraft.lunch_break_min,
    presenceDraft.coffee_break_min,
  );

  // ── Add entry ─────────────────────────────────────────────────────────────
  const submitNewEntry = async () => {
    if (!userEmail || !selectedDate || isValidated) return;
    if (!newEntry.organization_id || !newEntry.category_id) {
      toast.warning('Sélectionne une organisation et une catégorie');
      return;
    }
    if ((newEntry.project_label || '').trim().length < 5) {
      toast.warning('Le libellé projet doit faire au moins 5 caractères');
      return;
    }
    setAddingEntry(true);
    try {
      const res = await apiFetch(
        `/api/team/daily-log/${encodeURIComponent(userEmail)}/${selectedDate}/entry`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            organization_id: newEntry.organization_id,
            category_id: newEntry.category_id,
            project_label: newEntry.project_label.trim(),
            minutes: Number(newEntry.minutes) || 0,
            comment: (newEntry.comment || '').trim() || undefined,
          }),
        },
      );
      if (!res.ok) {
        const msg = await readApiError(res);
        toast.error(msg);
        return;
      }
      const data = await res.json();
      if (data.daily_log) setDailyLog(data.daily_log);
      setLastSavedAt(new Date());
      // reset libellé/minutes mais on garde org/cat pour les saisies en rafale
      setNewEntry((prev) => ({ ...prev, project_label: '', minutes: 30, comment: '' }));
      toast.success('Ligne ajoutée');
    } catch (e) {
      toast.error(`Erreur réseau : ${e.message}`);
    } finally {
      setAddingEntry(false);
    }
  };

  // ── Update entry inline ───────────────────────────────────────────────────
  const updateEntry = async (entryId, patch) => {
    if (!userEmail || !selectedDate || isValidated) return;
    const current = entries.find((e) => e.id === entryId);
    if (!current) return;
    const next = { ...current, ...patch };
    // payload = champs requis par backend (cf _validate_entry_payload)
    const body = {
      organization_id: next.organization_id,
      category_id: next.category_id,
      project_label: (next.project_label || '').trim(),
      minutes: Number(next.minutes) || 0,
      comment: (next.comment || '').trim() || undefined,
    };
    try {
      const res = await apiFetch(
        `/api/team/daily-log/${encodeURIComponent(userEmail)}/${selectedDate}/entry/${entryId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) {
        const msg = await readApiError(res);
        toast.error(msg);
        // recharger pour ré-aligner UI sur backend
        fetchDailyLog();
        return;
      }
      const data = await res.json();
      if (data.daily_log) setDailyLog(data.daily_log);
      setLastSavedAt(new Date());
    } catch (e) {
      toast.error(`Erreur réseau : ${e.message}`);
    }
  };

  // ── Delete entry ──────────────────────────────────────────────────────────
  const deleteEntry = async (entryId) => {
    if (!userEmail || !selectedDate || isValidated) return;
    const ok = await confirm('Supprimer cette ligne d\'activité ?');
    if (!ok) return;
    try {
      const res = await apiFetch(
        `/api/team/daily-log/${encodeURIComponent(userEmail)}/${selectedDate}/entry/${entryId}`,
        { method: 'DELETE' },
      );
      if (!res.ok) {
        const msg = await readApiError(res);
        toast.error(msg);
        return;
      }
      const data = await res.json();
      if (data.daily_log) setDailyLog(data.daily_log);
      toast.success('Ligne supprimée');
    } catch (e) {
      toast.error(`Erreur réseau : ${e.message}`);
    }
  };

  // ── Bloquants ─────────────────────────────────────────────────────────────
  const submitNewBlocker = async () => {
    if (!userEmail || !selectedDate || isValidated) return;
    if (!newBlocker.description || newBlocker.description.trim().length < 3) {
      toast.warning('Description du bloquant requise (≥ 3 caractères)');
      return;
    }
    setAddingBlocker(true);
    try {
      const res = await apiFetch(
        `/api/team/daily-log/${encodeURIComponent(userEmail)}/${selectedDate}/blocker`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: newBlocker.type,
            target_user_id: newBlocker.target_user_id || undefined,
            description: newBlocker.description.trim(),
          }),
        },
      );
      if (!res.ok) {
        const msg = await readApiError(res);
        toast.error(msg);
        return;
      }
      const data = await res.json();
      if (data.daily_log) setDailyLog(data.daily_log);
      setNewBlocker({ type: 'waiting', target_user_id: 'william.bernard@meteris.fr', description: '' });
      toast.success('Bloquant ajouté');
    } catch (e) {
      toast.error(`Erreur réseau : ${e.message}`);
    } finally {
      setAddingBlocker(false);
    }
  };

  const resolveBlocker = async (blockerId) => {
    if (!userEmail || !selectedDate) return;
    try {
      const res = await apiFetch(
        `/api/team/daily-log/${encodeURIComponent(userEmail)}/${selectedDate}/blocker/${blockerId}/resolve`,
        { method: 'PUT' },
      );
      if (!res.ok) {
        const msg = await readApiError(res);
        toast.error(msg);
        return;
      }
      const data = await res.json();
      if (data.daily_log) setDailyLog(data.daily_log);
      toast.success('Bloquant résolu');
    } catch (e) {
      toast.error(`Erreur réseau : ${e.message}`);
    }
  };

  // ── Validation finale ─────────────────────────────────────────────────────
  const validateDay = async () => {
    if (!userEmail || !selectedDate || isValidated) return;
    if (unallocated > 15) {
      toast.warning(
        `Impossible de valider : ${unallocated} min non allouées (tolérance 15 min)`,
      );
      return;
    }
    setValidating(true);
    setErreurValidation(null);
    try {
      const res = await apiFetch(
        `/api/team/daily-log/${encodeURIComponent(userEmail)}/${selectedDate}/validate`,
        { method: 'POST' },
      );
      if (!res.ok) {
        const msg = await readApiError(res);
        toast.error(msg);
        setErreurValidation(msg);
        return;
      }
      const data = await res.json();
      if (data.daily_log) setDailyLog(data.daily_log);
      toast.success('Journée validée');
    } catch (e) {
      toast.error(`Erreur réseau : ${e.message}`);
      setErreurValidation(`Erreur réseau : ${e.message}`);
    } finally {
      setValidating(false);
    }
  };

  // ── Styles inline locaux (non partagés) ───────────────────────────────────
  const zoneStyle = {
    background: '#fff',
    border: '1px solid var(--border-light)',
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 12,
  };
  const zoneTitle = {
    fontSize: 13,
    fontWeight: 700,
    color: '#1e293b',
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  };
  const smallInput = {
    ...EDIT_INPUT,
    width: 'auto',
    minWidth: 70,
    fontFamily: "'IBM Plex Mono', monospace",
  };
  const minutesInput = { ...smallInput, width: 70 };

  // Date affichage humain
  const humanDate = useMemo(() => {
    try {
      return new Date(`${selectedDate}T12:00:00`).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return selectedDate;
    }
  }, [selectedDate]);

  const techByEmail = useMemo(() => {
    const map = {};
    (techUsers || []).forEach((u) => {
      if (u.email) map[u.email] = u;
    });
    return map;
  }, [techUsers]);

  const labelForUser = (email) => {
    if (!email) return '—';
    const u = techByEmail[email];
    if (u) return u.name || u.first_name || email;
    return email;
  };

  const renderEntryRow = (entry) => {
    // La liste chargée par ensureCategoriesLoaded est filtrée aux catégories
    // actives (?active=true — cf ci-dessus) : une entrée historique portant
    // une catégorie désormais éteinte ("Depannage" etc., migration taxonomie
    // 2026-08) en serait absente. On la réinjecte pour l'affichage ET
    // l'édition de la ligne existante (même <select>) — sans la stocker dans
    // categoriesByOrg, pour ne pas la proposer au formulaire "nouvelle entry".
    // L'entrée ne porte pas de category_name (GET /api/team/daily-log ne
    // résout pas ce champ, contrairement à l'agrégation /overview) : repli
    // générique "Categorie (archivee)" via withArchivedCategory.
    const cats = withArchivedCategory(
      categoriesByOrg[entry.organization_id] || [],
      entry.category_id,
      entry.category_name,
    );
    // Suffixe pour forcer le remount des inputs uncontrolled (defaultValue)
    // lorsque le backend normalise la valeur (ex: minutes arrondis à 15).
    const refreshKey = entry.updated_at || entry.created_at || '0';
    return (
      <tr key={entry.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
        <td style={{ ...TD_STYLE, padding: '6px 8px' }}>
          <select
            value={entry.organization_id || ''}
            disabled={isValidated}
            onChange={(e) => {
              const newOrg = e.target.value;
              ensureCategoriesLoaded(newOrg);
              // Catégories org-scopées côté backend (_validate_entry_payload) :
              // changer réellement d'org invalide toujours l'ancienne catégorie,
              // active ou éteinte. resolveCategoryOnOrgChange ne préserve la
              // catégorie courante que si l'org n'a en fait pas changé — pour
              // ne pas reclasser en silence sur newCats[0] une entrée dont la
              // catégorie est simplement éteinte.
              const newCats = categoriesByOrg[newOrg] || [];
              updateEntry(entry.id, {
                organization_id: newOrg,
                category_id: resolveCategoryOnOrgChange(
                  newCats,
                  entry.organization_id,
                  newOrg,
                  entry.category_id,
                ),
              });
            }}
            style={{ ...SELECT_STYLE, padding: '4px 6px', fontSize: 12 }}
          >
            {organizations.map((o) => (
              <option key={o.id} value={o.id}>{o.code || o.name}</option>
            ))}
          </select>
        </td>
        <td style={{ ...TD_STYLE, padding: '6px 8px' }}>
          <select
            value={entry.category_id || ''}
            disabled={isValidated}
            onChange={(e) => updateEntry(entry.id, { category_id: e.target.value })}
            style={{ ...SELECT_STYLE, padding: '4px 6px', fontSize: 12, minWidth: 160 }}
          >
            <option value="">— Catégorie —</option>
            {cats.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </td>
        <td style={{ ...TD_STYLE, padding: '6px 8px' }}>
          <input
            key={`pl-${entry.id}-${refreshKey}`}
            type="text"
            defaultValue={entry.project_label || ''}
            disabled={isValidated}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== entry.project_label) updateEntry(entry.id, { project_label: v });
            }}
            placeholder="Libellé projet (5-80)"
            style={{ ...EDIT_INPUT, padding: '4px 6px', fontSize: 12 }}
          />
        </td>
        <td style={{ ...TD_STYLE, padding: '6px 8px', textAlign: 'right' }}>
          <input
            key={`mn-${entry.id}-${refreshKey}`}
            type="number"
            min={15}
            max={480}
            step={15}
            defaultValue={entry.minutes}
            disabled={isValidated}
            onBlur={(e) => {
              const v = Number(e.target.value);
              if (v && v !== entry.minutes) updateEntry(entry.id, { minutes: v });
            }}
            style={minutesInput}
          />
          <span style={{ marginLeft: 4, color: '#64748b', fontSize: 11 }}>min</span>
        </td>
        <td style={{ ...TD_STYLE, padding: '6px 8px' }}>
          <input
            key={`cm-${entry.id}-${refreshKey}`}
            type="text"
            defaultValue={entry.comment || ''}
            disabled={isValidated}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v !== (entry.comment || '')) updateEntry(entry.id, { comment: v });
            }}
            placeholder="Commentaire (optionnel)"
            style={{ ...EDIT_INPUT, padding: '4px 6px', fontSize: 12 }}
          />
        </td>
        <td style={{ ...TD_STYLE, padding: '6px 8px', textAlign: 'center' }}>
          {!isValidated && (
            <button
              onClick={() => deleteEntry(entry.id)}
              title="Supprimer cette ligne"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#dc2626',
                padding: 4,
                display: 'inline-flex',
              }}
            >
              <Trash2 size={14} />
            </button>
          )}
        </td>
      </tr>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: 'var(--bg)', padding: '12px 0' }}>
      {/* Appels non classés de mes postes : la facture du client attend qu'ils le soient (02/10/2026).
          Sous l'écran de blocage (lockedDate), ses liens vers l'historique des appels sont masqués. */}
      <AppelsNonClassesBloc depuisLeBlocage={!!lockedDate} />
      {/* Personnes notées « il doit passer » dont la relance est échue (13/10/2026) : une information,
          jamais un blocage de la validation. */}
      <PassagesARelancerBloc depuisLeBlocage={!!lockedDate} />
      {/* Header navigation jour + indicateurs */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 12,
          padding: '8px 12px',
          background: '#fff',
          border: '1px solid var(--border-light)',
          borderRadius: 8,
        }}
      >
        {!lockedDate && (
          <>
            <button
              onClick={() => setSelectedDate(shiftISODate(selectedDate, -1))}
              style={{ ...HDR_BTN, padding: '6px 10px', background: '#64748b' }}
              title="Jour précédent"
            >
              <ChevronLeft size={14} />
            </button>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value || todayISO())}
              style={{
                padding: '6px 10px',
                borderRadius: 6,
                border: '1px solid var(--border)',
                fontSize: 13,
                ...mono,
              }}
            />
            <button
              onClick={() => setSelectedDate(shiftISODate(selectedDate, 1))}
              style={{ ...HDR_BTN, padding: '6px 10px', background: '#64748b' }}
              title="Jour suivant"
            >
              <ChevronRight size={14} />
            </button>
            <button
              onClick={() => setSelectedDate(todayISO())}
              style={{ ...HDR_BTN, padding: '6px 12px', background: '#1a3a5c' }}
            >
              Aujourd&apos;hui
            </button>
            <span style={{ flex: 1, fontSize: 13, color: '#475569', textTransform: 'capitalize' }}>
              {humanDate}
            </span>
          </>
        )}

        {/* Indicateurs — toujours visibles, y compris en mode verrouillé */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <Indicator label="Actif" value={fmtMinutes(activeMinutes)} color="#1e293b" />
          <Indicator label="Tickets" value={fmtMinutes(trackedTotal)} color="#2563eb" />
          <Indicator label="Hors-ticket" value={fmtMinutes(entriesTotal)} color="#8b5cf6" />
          <Indicator
            label={overAllocated ? 'Sur-alloué' : 'Non alloué'}
            value={overAllocated ? fmtMinutes(-unallocated) : fmtMinutes(unallocated)}
            color={unallocatedColor}
            highlight={overAllocated || unallocated > 15}
          />
          {isValidated && (
            <span
              style={{
                background: '#dcfce7',
                color: '#166534',
                padding: '4px 10px',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
              title={
                dailyLog?.validated_at
                  ? `Validée le ${new Date(dailyLog.validated_at).toLocaleString('fr-FR')}`
                  : 'Validée'
              }
            >
              <Check size={14} /> Validée
            </span>
          )}
        </div>
      </div>

      {loading && (
        <div style={{ fontSize: 12, color: '#94a3b8', padding: '4px 12px', ...mono }}>
          Chargement journal…
        </div>
      )}

      {absence && (
        <div
          role="status"
          style={{
            background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: 8,
            padding: '10px 14px', margin: '8px 0', fontSize: 13, fontWeight: 600,
          }}
        >
          Jour d&apos;absence ({ABSENCE_LIBELLES[absence.reason] || 'absence'}) — rien à saisir : présence à 0,
          tu peux valider ta journée directement.
        </div>
      )}

      {/* Zone 1 — Présence (masquée un jour d'absence : le serveur refusait ses enregistrements) */}
      {!absence && (
      <div style={zoneStyle}>
        <div style={zoneTitle}>
          <Clock size={14} style={{ color: '#2563eb' }} /> Présence du jour
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={FIELD_LABEL}>Début</label>
            <input
              type="time"
              value={presenceDraft.presence_start}
              disabled={isValidated}
              onChange={(e) => setPresenceDraft((p) => ({ ...p, presence_start: e.target.value }))}
              onBlur={savePresenceSilent}
              style={smallInput}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Fin</label>
            <input
              type="time"
              value={presenceDraft.presence_end}
              disabled={isValidated}
              onChange={(e) => setPresenceDraft((p) => ({ ...p, presence_end: e.target.value }))}
              onBlur={savePresenceSilent}
              style={smallInput}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Pause déj (min)</label>
            <input
              type="number"
              min={0}
              max={240}
              step={5}
              value={presenceDraft.lunch_break_min}
              disabled={isValidated}
              onChange={(e) =>
                setPresenceDraft((p) => ({
                  ...p,
                  lunch_break_min: Number(e.target.value) || 0,
                }))
              }
              onBlur={savePresenceSilent}
              style={minutesInput}
            />
          </div>
          <div>
            <label style={FIELD_LABEL}>Pause café (min)</label>
            <input
              type="number"
              min={0}
              max={120}
              step={5}
              value={presenceDraft.coffee_break_min}
              disabled={isValidated}
              onChange={(e) =>
                setPresenceDraft((p) => ({
                  ...p,
                  coffee_break_min: Number(e.target.value) || 0,
                }))
              }
              onBlur={savePresenceSilent}
              style={minutesInput}
            />
          </div>
          <div style={{ ...mono, fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
            = {fmtMinutes(draftActive)} actif
          </div>
          <button
            onClick={() => savePresence({ silent: false })}
            disabled={savingPresence || isValidated}
            style={{
              ...PRIMARY_BTN,
              padding: '6px 12px',
              opacity: savingPresence || isValidated ? 0.5 : 1,
            }}
          >
            Enregistrer présence
          </button>
        </div>
      </div>
      )}

      {/* Zone 2 — Tickets clients déjà tracés (lecture seule) */}
      <div style={zoneStyle}>
        <div style={zoneTitle}>
          <FileText size={14} style={{ color: '#2563eb' }} />
          Tickets clients déjà tracés (lecture seule)
          <span style={{ marginLeft: 'auto', ...mono, fontSize: 12, color: '#2563eb' }}>
            Total {fmtMinutes(trackedTotal)}
          </span>
        </div>
        {trackedTasks.length === 0 ? (
          <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
            Aucun ticket tracé sur ce jour.
          </div>
        ) : (
          // Tableau triable et redimensionnable (demande William 09/10/2026) : trier par temps
          // ou par statut fait remonter ce qui a pris du temps et ce qui n'est pas terminé.
          <DataList
            data={trackedTasks}
            columns={COLONNES_TACHES_SUIVIES}
            rowKeyField="id"
            tableId="saisie-taches-du-jour"
            defaultSort={{ key: 'created_at', direction: 'asc' }}
            sansPagination
          />
        )}
      </div>

      {/* Zone 2 bis — Mes connexions aux postes ce jour-là (demande William 09/10/2026) */}
      <div style={zoneStyle}>
        <div style={zoneTitle}>
          <Monitor size={14} style={{ color: '#0ea5e9' }} />
          Mes connexions aux postes ce jour-là
        </div>
        <ConnexionsPostesDuJour date={selectedDate} userEmail={userEmail} taches={trackedTasks} />
      </div>

      {/* Zone 3 — Activités hors tickets */}
      <div style={zoneStyle}>
        <div style={zoneTitle}>
          <Calendar size={14} style={{ color: '#8b5cf6' }} />
          Activités hors tickets
          <span style={{ marginLeft: 'auto', ...mono, fontSize: 12, color: '#8b5cf6', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            <span>
              Total {fmtMinutes(entriesTotal)} · {entries.length} ligne{entries.length > 1 ? 's' : ''}
              {' · '}
              {entriesCategoryCount} catégorie{entriesCategoryCount > 1 ? 's' : ''} distincte{entriesCategoryCount > 1 ? 's' : ''}
              {entriesTotal > 0 && entriesCategoryCount >= 2 ? ' ✓' : ''}
            </span>
            {categoryWarning && (
              <span
                style={{
                  background: '#fff7ed',
                  color: '#b45309',
                  border: '1px solid #fcd34d',
                  borderRadius: 4,
                  padding: '2px 6px',
                  fontSize: 11,
                  fontWeight: 700,
                }}
                title="Au-delà de 2h, le backend exige au moins 2 catégories distinctes"
              >
                ⚠ 1 seule catégorie — au moins 2 requises au-delà de 2h
              </span>
            )}
          </span>
        </div>

        {entries.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
            <thead>
              <tr>
                <th style={{ ...TH_STYLE, width: 90 }}>Org</th>
                <th style={TH_STYLE}>Catégorie</th>
                <th style={TH_STYLE}>Projet / libellé</th>
                <th style={{ ...TH_STYLE, width: 110, textAlign: 'right' }}>Minutes</th>
                <th style={TH_STYLE}>Commentaire</th>
                <th style={{ ...TH_STYLE, width: 40 }} />
              </tr>
            </thead>
            <tbody>{entries.map(renderEntryRow)}</tbody>
          </table>
        )}

        {/* Ligne d'ajout */}
        {!isValidated && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-end',
              flexWrap: 'wrap',
              padding: 8,
              background: '#f8fafc',
              borderRadius: 6,
              border: '1px dashed #cbd5e1',
            }}
          >
            <div>
              <label style={FIELD_LABEL}>Org</label>
              <select
                value={newEntry.organization_id}
                onChange={(e) =>
                  setNewEntry((p) => ({ ...p, organization_id: e.target.value, category_id: '' }))
                }
                style={{ ...SELECT_STYLE, padding: '6px 8px', fontSize: 12 }}
              >
                <option value="">—</option>
                {organizations.map((o) => (
                  <option key={o.id} value={o.id}>{o.code || o.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={FIELD_LABEL}>Catégorie</label>
              <select
                value={newEntry.category_id}
                onChange={(e) => setNewEntry((p) => ({ ...p, category_id: e.target.value }))}
                style={{ ...SELECT_STYLE, padding: '6px 8px', fontSize: 12, minWidth: 160 }}
                disabled={!newEntry.organization_id}
              >
                <option value="">—</option>
                {(categoriesByOrg[newEntry.organization_id] || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={FIELD_LABEL}>Projet / libellé (5-80)</label>
              <input
                type="text"
                value={newEntry.project_label}
                onChange={(e) => setNewEntry((p) => ({ ...p, project_label: e.target.value }))}
                placeholder="ex: Refonte landing meteris-pro"
                style={{ ...EDIT_INPUT, padding: '6px 8px', fontSize: 12 }}
              />
            </div>
            <div>
              <label style={FIELD_LABEL}>Minutes</label>
              <input
                type="number"
                min={15}
                max={480}
                step={15}
                value={newEntry.minutes}
                onChange={(e) =>
                  setNewEntry((p) => ({ ...p, minutes: Number(e.target.value) || 0 }))
                }
                style={minutesInput}
              />
            </div>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={FIELD_LABEL}>Commentaire (optionnel)</label>
              <input
                type="text"
                value={newEntry.comment}
                onChange={(e) => setNewEntry((p) => ({ ...p, comment: e.target.value }))}
                placeholder="Précisions"
                style={{ ...EDIT_INPUT, padding: '6px 8px', fontSize: 12 }}
              />
            </div>
            <button
              onClick={submitNewEntry}
              disabled={addingEntry}
              style={{
                ...SUCCESS_BTN,
                padding: '7px 14px',
                opacity: addingEntry ? 0.6 : 1,
              }}
            >
              <Plus size={14} /> Ajouter
            </button>
          </div>
        )}
      </div>

      {/* Zone 4 — Bloquants & demandes */}
      <div style={zoneStyle}>
        <div style={zoneTitle}>
          <AlertCircle size={14} style={{ color: '#f59e0b' }} />
          Bloquants &amp; demandes
        </div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
          <label
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              cursor: isValidated ? 'default' : 'pointer',
              fontSize: 13,
              color: !hasBlockersUi ? '#059669' : '#475569',
              fontWeight: !hasBlockersUi ? 700 : 500,
            }}
          >
            <input
              type="radio"
              name="has-blockers"
              checked={!hasBlockersUi}
              disabled={isValidated || blockers.some((b) => b.status === 'open')}
              onChange={() => setHasBlockersUi(false)}
            />
            Rien de bloquant
          </label>
          <label
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              cursor: isValidated ? 'default' : 'pointer',
              fontSize: 13,
              color: hasBlockersUi ? '#f59e0b' : '#475569',
              fontWeight: hasBlockersUi ? 700 : 500,
            }}
          >
            <input
              type="radio"
              name="has-blockers"
              checked={hasBlockersUi}
              disabled={isValidated}
              onChange={() => setHasBlockersUi(true)}
            />
            J&apos;ai des choses qui bloquent
          </label>
        </div>

        {hasBlockersUi && (
          <>
            {blockers.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 8 }}>
                <thead>
                  <tr>
                    <th style={{ ...TH_STYLE, width: 120 }}>Type</th>
                    <th style={{ ...TH_STYLE, width: 180 }}>Destinataire</th>
                    <th style={TH_STYLE}>Description</th>
                    <th style={{ ...TH_STYLE, width: 90 }}>Statut</th>
                    <th style={{ ...TH_STYLE, width: 90 }} />
                  </tr>
                </thead>
                <tbody>
                  {blockers.map((b) => (
                    <tr key={b.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ ...TD_STYLE, padding: '6px 8px' }}>
                        {BLOCKER_TYPE_LABELS[b.type] || b.type}
                      </td>
                      <td style={{ ...TD_STYLE, padding: '6px 8px' }}>
                        {labelForUser(b.target_user_id)}
                      </td>
                      <td style={{ ...TD_STYLE, padding: '6px 8px' }}>{b.description}</td>
                      <td style={{ ...TD_STYLE, padding: '6px 8px' }}>
                        {b.status === 'open' ? (
                          <span style={{ color: '#f59e0b', fontWeight: 700 }}>Ouvert</span>
                        ) : (
                          <span style={{ color: '#059669' }}>Résolu</span>
                        )}
                      </td>
                      <td style={{ ...TD_STYLE, padding: '6px 8px', textAlign: 'right' }}>
                        {b.status === 'open' && (
                          <button
                            onClick={() => resolveBlocker(b.id)}
                            style={{
                              ...HDR_BTN,
                              padding: '4px 10px',
                              background: '#059669',
                              fontSize: 11,
                            }}
                          >
                            <Check size={12} /> Résoudre
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {!isValidated && (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'flex-end',
                  flexWrap: 'wrap',
                  padding: 8,
                  background: '#fffbeb',
                  borderRadius: 6,
                  border: '1px dashed #fcd34d',
                }}
              >
                <div>
                  <label style={FIELD_LABEL}>Type</label>
                  <select
                    value={newBlocker.type}
                    onChange={(e) => setNewBlocker((p) => ({ ...p, type: e.target.value }))}
                    style={{ ...SELECT_STYLE, padding: '6px 8px', fontSize: 12 }}
                  >
                    <option value="waiting">J&apos;attends</option>
                    <option value="blocking">Bloquant</option>
                    <option value="suggestion">Suggestion</option>
                  </select>
                </div>
                <div>
                  <label style={FIELD_LABEL}>Destinataire</label>
                  <select
                    value={newBlocker.target_user_id}
                    onChange={(e) =>
                      setNewBlocker((p) => ({ ...p, target_user_id: e.target.value }))
                    }
                    style={{ ...SELECT_STYLE, padding: '6px 8px', fontSize: 12, minWidth: 160 }}
                  >
                    <option value="">— (sans cible) —</option>
                    {techUsers
                      .filter((u) => u.email && u.email !== userEmail)
                      .map((u) => (
                        <option key={u.id || u.email} value={u.email}>
                          {u.name || u.first_name || u.email}
                        </option>
                      ))}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <label style={FIELD_LABEL}>Description</label>
                  <input
                    type="text"
                    value={newBlocker.description}
                    onChange={(e) =>
                      setNewBlocker((p) => ({ ...p, description: e.target.value }))
                    }
                    placeholder="ex: Attends validation maquette landing"
                    style={{ ...EDIT_INPUT, padding: '6px 8px', fontSize: 12 }}
                  />
                </div>
                <button
                  onClick={submitNewBlocker}
                  disabled={addingBlocker}
                  style={{
                    ...HDR_BTN,
                    background: '#f59e0b',
                    padding: '7px 14px',
                    opacity: addingBlocker ? 0.6 : 1,
                  }}
                >
                  <Plus size={14} /> Ajouter bloquant
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bouton validation finale */}
      {!isValidated && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '8px 12px',
            background: '#fff',
            border: '1px solid var(--border-light)',
            borderRadius: 8,
          }}
        >
          {/* Indicateur autosave (Fix 5) — discret à gauche */}
          <span
            style={{
              fontSize: 11,
              color: '#94a3b8',
              marginRight: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              ...mono,
            }}
            title="Toutes les modifications sont sauvegardées automatiquement"
          >
            💾 Brouillon enregistré automatiquement
            {lastSavedAt && (
              <span style={{ color: '#64748b' }}>
                {' · dernier enregistrement à '}
                {lastSavedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </span>
          {overAllocated && (
            <span
              style={{
                fontSize: 12,
                color: '#dc2626',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <AlertTriangle size={14} />
              Sur-alloué de {fmtMinutes(-unallocated)} — réduis tes lignes ou augmente la présence
            </span>
          )}
          {!overAllocated && unallocated > 15 && (
            <span
              style={{
                fontSize: 12,
                color: '#dc2626',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <AlertTriangle size={14} />
              {fmtMinutes(unallocated)} non alloué — ajoute des lignes ou ajuste la présence
            </span>
          )}
          {erreurValidation && (
            <span
              role="alert"
              style={{
                fontSize: 12,
                color: '#dc2626',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                maxWidth: 520,
              }}
            >
              <AlertTriangle size={14} style={{ flexShrink: 0 }} />
              {erreurValidation}
            </span>
          )}
          <button
            data-tour="valider-journee"
            onClick={validateDay}
            disabled={validating || unallocated > 15 || overAllocated}
            title={
              overAllocated
                ? `Impossible de valider : sur-alloué de ${fmtMinutes(-unallocated)}`
                : unallocated > 15
                ? `Impossible de valider : ${unallocated} min non alloué (tolérance 15 min)`
                : 'Valider la journée'
            }
            style={{
              ...SUCCESS_BTN,
              padding: '8px 16px',
              opacity: validating || unallocated > 15 || overAllocated ? 0.5 : 1,
              cursor: unallocated > 15 || overAllocated ? 'not-allowed' : 'pointer',
            }}
          >
            <Check size={14} /> Valider ma journée
          </button>
        </div>
      )}
    </div>
  );
}

const BLOCKER_TYPE_LABELS = {
  waiting: "J'attends",
  blocking: 'Bloquant',
  suggestion: 'Suggestion',
};

// ── Petit composant indicateur ─────────────────────────────────────────────

function Indicator({ label, value, color, highlight }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: highlight ? '4px 10px' : '4px 8px',
        background: highlight ? '#fff7ed' : 'transparent',
        borderRadius: 6,
        border: highlight ? `1px solid ${color}` : '1px solid transparent',
      }}
    >
      <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ ...mono, fontSize: 13, fontWeight: 700, color }}>{value}</span>
    </div>
  );
}
